import { supabase } from './supabase';
import type { MediaItem } from './mediaLibrary';

const USER_UPLOADS_BUCKET = 'user-uploads';

export type BusinessPhotoCategory =
  | 'bookings'
  | 'field_quotes'
  | 'proposals'
  | 'projects'
  | 'uploads';

export interface BusinessPhotoCategoryOption {
  id: BusinessPhotoCategory;
  label: string;
  description: string;
}

export const BUSINESS_PHOTO_CATEGORIES: BusinessPhotoCategoryOption[] = [
  {
    id: 'bookings',
    label: 'Booking images',
    description: 'Photos attached to bookings',
  },
  {
    id: 'field_quotes',
    label: 'Field & quote images',
    description: 'Photos from field quotes',
  },
  {
    id: 'proposals',
    label: 'Proposal images',
    description: 'Proposal and line-item photos',
  },
  {
    id: 'projects',
    label: 'Job / project images',
    description: 'Photos attached to jobs',
  },
  {
    id: 'uploads',
    label: 'Other uploads',
    description: 'General uploaded images',
  },
];

export interface BusinessPhotoItem {
  /** Stable selection key: `${category}:${id}` */
  key: string;
  id: string;
  category: BusinessPhotoCategory;
  url: string;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  storagePath: string | null;
  createdAt: string;
}

function publicUrlFromUserUploads(filePath: string): string {
  const { data } = supabase.storage.from(USER_UPLOADS_BUCKET).getPublicUrl(filePath);
  return data.publicUrl;
}

function mapRow(params: {
  category: BusinessPhotoCategory;
  id: string;
  fileName?: string | null;
  filePath?: string | null;
  url?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  createdAt?: string | null;
}): BusinessPhotoItem | null {
  const url =
    (params.url && params.url.trim()) ||
    (params.filePath ? publicUrlFromUserUploads(params.filePath) : '');
  if (!url) return null;
  return {
    key: `${params.category}:${params.id}`,
    id: params.id,
    category: params.category,
    url,
    fileName: params.fileName || null,
    mimeType: params.mimeType || null,
    fileSize: params.fileSize ?? null,
    storagePath: params.filePath || null,
    createdAt: params.createdAt || new Date(0).toISOString(),
  };
}

/**
 * Load photos for one category, newest first.
 * Queries only that source table (joined to business when needed).
 */
export async function listBusinessPhotosByCategory(
  businessId: string,
  category: BusinessPhotoCategory,
  opts?: { limit?: number; offset?: number },
): Promise<BusinessPhotoItem[]> {
  const limit = opts?.limit ?? 60;
  const offset = opts?.offset ?? 0;
  const from = offset;
  const to = offset + limit - 1;

  if (category === 'bookings') {
    const { data, error } = await supabase
      .from('booking_photos')
      .select(
        'id, file_name, file_path, file_size, mime_type, created_at, bookings!inner(business_id)',
      )
      .eq('bookings.business_id', businessId)
      .order('created_at', { ascending: false })
      .range(from, to);
    if (error) throw error;
    return (data || [])
      .map((row) =>
        mapRow({
          category,
          id: row.id as string,
          fileName: row.file_name as string | null,
          filePath: row.file_path as string | null,
          mimeType: row.mime_type as string | null,
          fileSize: row.file_size as number | null,
          createdAt: row.created_at as string,
        }),
      )
      .filter(Boolean) as BusinessPhotoItem[];
  }

  if (category === 'field_quotes') {
    const { data, error } = await supabase
      .from('field_quote_photos')
      .select('id, file_name, file_path, file_size, mime_type, created_at')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .range(from, to);
    if (error) throw error;
    return (data || [])
      .map((row) =>
        mapRow({
          category,
          id: row.id as string,
          fileName: row.file_name as string | null,
          filePath: row.file_path as string | null,
          mimeType: row.mime_type as string | null,
          fileSize: row.file_size as number | null,
          createdAt: row.created_at as string,
        }),
      )
      .filter(Boolean) as BusinessPhotoItem[];
  }

  if (category === 'proposals') {
    // Proposal-level + line-item photos in one list.
    const [proposalRes, serviceRes] = await Promise.all([
      supabase
        .from('proposal_photos')
        .select(
          'id, file_name, file_path, file_size, mime_type, created_at, proposals!inner(business_id)',
        )
        .eq('proposals.business_id', businessId)
        .order('created_at', { ascending: false })
        .range(from, to),
      supabase
        .from('proposal_service_photos')
        .select('id, file_name, file_path, file_size, mime_type, created_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .range(from, to),
    ]);
    if (proposalRes.error) throw proposalRes.error;
    if (serviceRes.error) throw serviceRes.error;

    const mapped = [
      ...(proposalRes.data || []).map((row) =>
        mapRow({
          category,
          id: `proposal:${row.id}`,
          fileName: row.file_name as string | null,
          filePath: row.file_path as string | null,
          mimeType: row.mime_type as string | null,
          fileSize: row.file_size as number | null,
          createdAt: row.created_at as string,
        }),
      ),
      ...(serviceRes.data || []).map((row) =>
        mapRow({
          category,
          id: `service:${row.id}`,
          fileName: row.file_name as string | null,
          filePath: row.file_path as string | null,
          mimeType: row.mime_type as string | null,
          fileSize: row.file_size as number | null,
          createdAt: row.created_at as string,
        }),
      ),
    ].filter(Boolean) as BusinessPhotoItem[];

    mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return mapped.slice(0, limit);
  }

  if (category === 'projects') {
    const { data, error } = await supabase
      .from('project_photos')
      .select(
        'id, file_name, file_path, file_size, mime_type, created_at, projects!inner(business_id)',
      )
      .eq('projects.business_id', businessId)
      .order('created_at', { ascending: false })
      .range(from, to);
    if (error) throw error;
    return (data || [])
      .map((row) =>
        mapRow({
          category,
          id: row.id as string,
          fileName: row.file_name as string | null,
          filePath: row.file_path as string | null,
          mimeType: row.mime_type as string | null,
          fileSize: row.file_size as number | null,
          createdAt: row.created_at as string,
        }),
      )
      .filter(Boolean) as BusinessPhotoItem[];
  }

  // uploads
  const { data, error } = await supabase
    .from('user_uploaded_images')
    .select('id, name, url, file_size, mime_type, created_at')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .range(from, to);
  if (error) throw error;
  return (data || [])
    .map((row) =>
      mapRow({
        category,
        id: row.id as string,
        fileName: (row.name as string | null) || null,
        url: row.url as string | null,
        mimeType: row.mime_type as string | null,
        fileSize: row.file_size as number | null,
        createdAt: row.created_at as string,
      }),
    )
    .filter(Boolean) as BusinessPhotoItem[];
}

/**
 * Add selected business photos into media_library (link existing URLs; no re-upload).
 * Skips URLs already present in the library for this business.
 */
export async function importBusinessPhotosToMediaLibrary(
  businessId: string,
  photos: BusinessPhotoItem[],
): Promise<{ created: MediaItem[]; skipped: number }> {
  if (photos.length === 0) return { created: [], skipped: 0 };

  const urls = [...new Set(photos.map((p) => p.url).filter(Boolean))];
  const { data: existing, error: existingErr } = await supabase
    .from('media_library')
    .select('id, url')
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .in('url', urls);
  if (existingErr) throw existingErr;

  const existingUrls = new Set((existing || []).map((r) => r.url as string));
  const toInsert = photos.filter((p) => p.url && !existingUrls.has(p.url));
  const skipped = photos.length - toInsert.length;
  if (toInsert.length === 0) return { created: [], skipped };

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;

  const rows = toInsert.map((p) => ({
    business_id: businessId,
    url: p.url,
    storage_path: p.storagePath,
    file_name: p.fileName,
    mime_type: p.mimeType,
    file_size: p.fileSize,
    source: p.category,
    created_by: userId,
  }));

  const { data: created, error } = await supabase
    .from('media_library')
    .insert(rows)
    .select();
  if (error) throw error;

  return { created: (created || []) as MediaItem[], skipped };
}
